import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from '@/stores'
import { useToast } from '@/components/providers/ToastProvider'
import { useRecovery } from '@/components/providers/RecoveryProvider'
import { useSelectionCaptureReady } from '@/hooks/useSelectionCaptureReady'
import { startAutoReload } from '@/lib/appReload'
import { RecordConsentModal } from './RecordConsentModal'
import { NetworkQualityIndicator } from '@/components/NetworkQualityIndicator/NetworkQualityIndicator'
import { useRecordingSession } from './useRecordingSession'
import { useRecordingRecovery } from './useRecordingRecovery'

interface RecordButtonProps {
  className?: string
  onStreamChange?: (stream: MediaStream | null) => void
  onRecordingChange?: (isRecording: boolean) => void
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function RecordButton({
  className = '',
  onStreamChange,
  onRecordingChange,
}: RecordButtonProps) {
  const setRecording = useStore((state) => state.setRecording)
  const toast = useToast()
  const { showToast } = toast
  const { isRecovering } = useRecovery()
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false)
  const forcedStopPendingRef = useRef(false)

  // Mirror NoteInput's gate: outside the optional-taxonomy fallback build the
  // recorder stays disabled until a full asset/indication/KIQ selection exists
  // AND the selected asset is still within its active period (present in the
  // live taxonomy). Recording a closed/absent asset would be rejected with 403.
  const captureReady = useSelectionCaptureReady()
  const selectionGated = !captureReady

  const startSaveFailureRefresh = useCallback(() => {
    void startAutoReload({
      reason: 'save-failure',
      toast,
      toastType: 'error',
      message:
        'Failed to save recording. Refreshing in {s}s. Your device may have suspended this session. The app will refresh automatically. Please try again after the refresh.',
    })
  }, [toast])

  const session = useRecordingSession({
    onStreamChange,
    onSaveFailure: startSaveFailureRefresh,
    forcedStopPendingRef,
  })

  useRecordingRecovery({
    isRecording: session.isRecording,
    stopRecording: session.stopRecording,
    getSessionId: session.getSessionId,
    getLastStoppedSessionId: session.getLastStoppedSessionId,
    pendingConsentRef: session.pendingConsentRef,
    forcedStopPendingRef,
  })

  // Warn user before closing tab while recording
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (session.isRecording) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [session.isRecording])

  // Sync recording state with parent and global store
  useEffect(() => {
    onRecordingChange?.(session.isRecording)
  }, [session.isRecording, onRecordingChange])

  useEffect(() => {
    setRecording(session.isRecording)
  }, [session.isRecording, setRecording])

  const handleClick = () => {
    if (isRecovering) {
      showToast('App is recovering, please wait...', 'info')
      return
    }
    if (!session.hasPermission && !session.isRecording) {
      showToast('Microphone permission is required before recording', 'warning')
      return
    }
    if (!session.isRecording && selectionGated) {
      showToast(
        'Please select an active asset before recording',
        'warning',
      )
      return
    }
    if (session.isRecording) {
      session.stopRecording()
      onRecordingChange?.(false)
    } else {
      setIsConsentModalOpen(true)
    }
  }

  const handleStop = () => {
    session.stopRecording()
    onRecordingChange?.(false)
  }

  return (
    <>
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        <button
          type="button"
          onClick={handleClick}
          disabled={
            !session.isRecording && (!session.hasPermission || selectionGated)
          }
          className={`
            relative flex h-20 w-20 items-center justify-center rounded-full
            text-white shadow-xl transition-all duration-200
            focus:outline-none focus:ring-4 focus:ring-orange/50 focus:ring-offset-4 focus:ring-offset-navy
            disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none
            ${
              session.isRecording
                ? 'bg-red-600 hover:bg-red-700 scale-105 shadow-red-600/50'
                : 'bg-orange hover:bg-orange-dark scale-100 shadow-orange/50'
            }
          `}
          aria-label={session.isRecording ? 'Stop recording' : 'Start recording'}
          aria-pressed={session.isRecording}
        >
          {session.isRecording ? (
            <div className="h-6 w-6 rounded bg-white" />
          ) : (
            <div className="h-0 w-0 border-l-[10px] border-l-white border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent ml-1" />
          )}
          {session.isRecording && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            </span>
          )}
        </button>

        {session.isRecording && (
          <div className="flex flex-col items-center gap-3">
            <div className="text-xl font-mono font-bold text-neon-cyan">
              {formatDuration(session.durationMs)}
            </div>
            <NetworkQualityIndicator />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleStop}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                aria-label="Stop recording"
              >
                Stop
              </button>
            </div>
          </div>
        )}

        {!session.hasPermission && !session.isRecording && (
          <p className="text-xs text-gray-400 text-center max-w-xs">
            Microphone permission required
          </p>
        )}

        {session.hasPermission && selectionGated && !session.isRecording && (
          <p className="text-xs text-gray-400 text-center max-w-xs">
            Select an active asset to enable recording.
          </p>
        )}
      </div>

      <RecordConsentModal
        isOpen={isConsentModalOpen}
        onClose={() => setIsConsentModalOpen(false)}
        onConfirm={(metadata) => {
          setIsConsentModalOpen(false)
          session.startWithConsent(metadata)
          onRecordingChange?.(true)
        }}
      />
    </>
  )
}
